import BooleanField from '@/components/booleanField';
import JsonField from '@/components/jsonField/jsonField';
import MultiSelectField from '@/components/muiltipleSelectField';
import NumberField from '@/components/numberField';
import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import { useApiContext } from '@/contexts/apiContext';
import { FormLabel, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Embedding, Llm } from '../entityType';

type Props = {
  entityDefId: string;
  settingDef: any;
  settings: any;
  errors: any;
  onChange: (settingName: string, value: any) => void;
  index: number;
};

const RESPONSE_TONES = [
  'Formal/Professional',
  'Objective',
  'Journalistic',
  'Informative/Educational',
  'Encouraging/Supportive',
  'Friendly/Casual',
  'Humorous/Playful',
  'Sympathetic/Empathetic',
];

const TEXT_EXTRACTION_API = [
  { value: 'apachePdfBox', label: 'Apache PDF Box' },
  { value: 'azureDocumentIntelligence', label: 'Azure Document Intelligence' },
  { value: 'apacheTikka', label: 'Apache Tikka' },
];

const PDF_CONVERSION_API = [
  { value: 'defaultConversion', label: 'Default Conversion' },
  { value: 'azureDocumentIntelligence', label: 'Azure Document Intelligence' },
];

const TRANSLATION_API = [
  { value: 'google', label: 'Google translation' },
  { value: 'azure', label: 'Azure translation', disabled: true },
];

const SettingField = ({ entityDefId, settingDef, settings, errors = {}, onChange, index }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [selectOptions, setSelectOptions] = useState<any[]>([]);

  const maxFileSize = parseInt(import.meta.env.VITE_MAX_FILE_UPLOAD_SIZE);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getConversationLlms = async () => {
      try {
        setLoading(true);
        const llms = await apiContext.getConversationLlms();
        setSelectOptions(
          llms.map((llm) => {
            return { value: llm, label: llm };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getDataExtractionLlms = async () => {
      try {
        setLoading(true);
        const llms = await apiContext.getDataExtractionLlms();
        setSelectOptions(
          llms.map((llm) => {
            return { value: llm, label: t(`common.llm.${llm}`) };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getConversationLlmModels = async (llm: Llm) => {
      try {
        setLoading(true);
        const llmModels = await apiContext.getConversationLlmModels(llm);
        setSelectOptions(
          llmModels.map((model) => {
            return { value: model.modelCode, label: model.modelName };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getDataExtractionLlmModels = async (llm: Llm) => {
      try {
        setLoading(true);
        const llmModels = await apiContext.getDataExtractionLlmModels(llm);
        setSelectOptions(
          llmModels.map((model) => {
            return { value: model.modelCode, label: model.modelName };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getEmbeddingsLlms = async () => {
      try {
        setLoading(true);
        const llms = await apiContext.getEmbeddingsLlms();
        setSelectOptions(
          llms.map((llm) => {
            return { value: llm, label: llm };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getEmbeddingModels = async (embeddingApi: Embedding) => {
      try {
        setLoading(true);
        const models = await apiContext.getEmbeddingsModels(embeddingApi);
        setSelectOptions(
          models.map((model) => {
            return { value: model.modelCode, label: model.modelName };
          }),
        );
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    const getFileTypes = async () => {
      try {
        setLoading(true);
        const fileTypes = await apiContext.getFileTypes();
        const fileMimeTypes = await apiContext.getFileMimeTypes();
        const allFileTypes = [...fileTypes.map((ft) => ft.name), ...fileMimeTypes.map((ft) => ft.name)];
        setSelectOptions(allFileTypes);
      } catch (error: any) {
        setError(error);
      }
      setLoading(false);
    };

    if (settingDef.type === 'l' || settingDef.type === 'ml') {
      if (Array.isArray(settingDef.valueOptions)) {
        setSelectOptions(settingDef.valueOptions);
      } else if (settingDef.valueOptions === 'acceptedFileTypes') {
        getFileTypes();
      } else if (settingDef.valueOptions === 'dataExtractionLlms') {
        getDataExtractionLlms();
      } else if (settingDef.valueOptions === 'conversationLlms') {
        getConversationLlms();
      } else if (settingDef.valueOptions === 'openAiConversationModels') {
        getConversationLlmModels(Llm.openai);
      } else if (settingDef.valueOptions === 'openAiDataExtractionModels') {
        getDataExtractionLlmModels(Llm.openai);
      } else if (settingDef.valueOptions === 'azureOpenAiConversationModels') {
        getConversationLlmModels(Llm.azureopenai);
      } else if (settingDef.valueOptions === 'azureOpenAiDataExtractionModels') {
        getDataExtractionLlmModels(Llm.azureopenai);
      } else if (settingDef.valueOptions === 'geminiAiConversationModels') {
        getConversationLlmModels(Llm.geminiai);
      } else if (settingDef.valueOptions === 'geminiAiDataExtractionModels') {
        getDataExtractionLlmModels(Llm.geminiai);
      } else if (settingDef.valueOptions === 'claudeAiConversationModels') {
        getConversationLlmModels(Llm.claudeai);
      } else if (settingDef.valueOptions === 'claudeAiDataExtractionModels') {
        getDataExtractionLlmModels(Llm.claudeai);
      } else if (settingDef.valueOptions === 'conversationResponseTone') {
        setSelectOptions(RESPONSE_TONES);
      } else if (settingDef.valueOptions === 'textExtractionApi') {
        setSelectOptions(TEXT_EXTRACTION_API);
      } else if (settingDef.valueOptions === 'pdfConversionApi') {
        setSelectOptions(PDF_CONVERSION_API);
      } else if (settingDef.valueOptions === 'translationApi') {
        setSelectOptions(TRANSLATION_API);
      } else if (settingDef.valueOptions === 'embeddingLlms') {
        getEmbeddingsLlms();
      } else if (settingDef.valueOptions === 'openAiEmbeddingModels') {
        getEmbeddingModels(Embedding.openai);
      } else if (settingDef.valueOptions === 'azureOpenAiEmbeddingModels') {
        getEmbeddingModels(Embedding.azureopenai);
      } else if (settingDef.valueOptions === 'geminiAiEmbeddingModels') {
        getEmbeddingModels(Embedding.geminiai);
      }
    }
  }, [settingDef.type]);

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      onChange(settingDef.name, await file.text());
      errors.DOCUMENT_TRANSLATION_GCP_ACCOUNT_JSON = '';
    }
  };

  return (
    <>
      <Grid sx={{ borderTop: index > 0 ? 1 : 0, borderColor: 'divider' }} size={settingDef.type === 't' ? 12 : 6}>
        <FormLabel required={settingDef.required}>{settingDef.label}</FormLabel>
      </Grid>
      <Grid sx={{ borderTop: index > 0 ? 1 : 0, borderColor: 'divider' }} size={settingDef.type === 't' ? 12 : 6}>
        {!settingDef.type ||
          (settingDef.type === 's' && (
            <TextField
              value={settings[settingDef.name]}
              required={settingDef.required}
              error={errors[settingDef.name]}
              onChange={(value) => onChange(settingDef.name, value)}
              type={settingDef.sensitive ? 'password' : 'text'}
              disabled={settingDef.disabled}
            />
          ))}
        {settingDef.type === 't' && (
          <TextField
            value={settings[settingDef.name]}
            required={settingDef.required}
            error={errors[settingDef.name]}
            onChange={(value) => onChange(settingDef.name, value)}
            multiline
            maxRows={6}
          />
        )}
        {settingDef.type === 'n' && (
          <NumberField
            value={settings[settingDef.name]}
            required={settingDef.required}
            error={errors[settingDef.name]}
            min={settingDef.minValue}
            max={settingDef.maxValue}
            onChange={(value) => onChange(settingDef.name, value)}
            disabled={settingDef.disabled}
          />
        )}
        {settingDef.type === 'b' && (
          <BooleanField
            value={settings[settingDef.name]}
            required={settingDef.required}
            error={errors[settingDef.name]}
            onChange={(value) => onChange(settingDef.name, value)}
            disabled={settingDef.disabled}
          />
        )}
        {settingDef.type === 'l' && (
          <SelectField
            options={selectOptions}
            value={settings[settingDef.name]}
            required={settingDef.required}
            error={errors[settingDef.name]}
            onChange={(value) => onChange(settingDef.name, value)}
            disabled={settingDef.disabled}
          />
        )}
        {settingDef.type === 'ml' && (
          <MultiSelectField
            value={
              settings[settingDef.name]
                ? Array.isArray(settings[settingDef.name])
                  ? settings[settingDef.name]
                  : settings[settingDef.name].split(',')
                : []
            }
            options={selectOptions}
            required={settingDef.required}
            error={errors[settingDef.name]}
            onChange={(value) => onChange(settingDef.name, value)}
            disabled={settingDef.disabled}
          />
        )}

        {settingDef.type === 'json' && (
          <JsonField
            value={settings[settingDef.name]}
            required={settingDef.required}
            error={errors[settingDef.name]}
            onChange={(value) => onChange(settingDef.name, value)}
            disabled={settingDef.disabled}
            rows={4}
          />
        )}
      </Grid>
    </>
  );
};

export default SettingField;
